import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Header } from '../../components/Header/Header';
import { Footer } from '../../components/Footer/Footer';
import { useAuth } from '../../../context/AuthContext';
import {
  cancelFriendRequest,
  fetchRelationships,
  respondToFriendRequest,
  sendFriendRequest,
  endFriendship
} from '../../../service/friendService';
import './Friends.css';

const Friends = () => {
  const { user } = useAuth();
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [menuOpenFor, setMenuOpenFor] = useState(null);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await fetchRelationships(user.uid);
      setRelationships(data);
    } catch (err) {
      console.error('Erro ao carregar amizades', err);
      setError('Não foi possível carregar seus amigos agora.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, loadData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.friend-menu-wrapper')) {
        setMenuOpenFor(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const pendingReceived = useMemo(
    () => relationships.filter((rel) => rel.status === 'pendente' && !rel.isRequester),
    [relationships]
  );

  const pendingSent = useMemo(
    () => relationships.filter((rel) => rel.status === 'pendente' && rel.isRequester),
    [relationships]
  );

  const activeFriends = useMemo(
    () => relationships.filter((rel) => rel.status === 'ativo'),
    [relationships]
  );

  const handleSendRequest = async (event) => {
    event.preventDefault();
    setError('');
    setActionMessage('');
    if (!email) return;
    try {
      await sendFriendRequest(user, email);
      setEmail('');
      setActionMessage('Convite enviado com sucesso.');
      await loadData();
    } catch (err) {
      console.error('Erro ao enviar convite', err);
      setError(err.message || 'Não foi possível enviar o convite.');
    }
  };

  const handleRespond = async (relationId, accept) => {
    try {
      await respondToFriendRequest(relationId, accept);
      await loadData();
    } catch (err) {
      console.error('Erro ao responder convite', err);
      setError('Não foi possível atualizar a solicitação agora.');
    }
  };

  const handleCancel = async (relationId) => {
    try {
      await cancelFriendRequest(relationId);
      await loadData();
    } catch (err) {
      console.error('Erro ao cancelar convite', err);
      setError('Não foi possível cancelar o convite agora.');
    }
  };

  const handleToggleFriendMenu = (event, relationId) => {
    event.stopPropagation();
    setMenuOpenFor((prev) => (prev === relationId ? null : relationId));
  };

  const handleEndFriendship = async (event, relation) => {
    event.stopPropagation();
    setMenuOpenFor(null);
    setActionMessage('');
    setError('');
    try {
      await endFriendship(relation.id);
      setActionMessage(`${relation.otherUser.nome} removido da sua lista.`);
      await loadData();
    } catch (err) {
      console.error('Erro ao encerrar amizade', err);
      setError('Não foi possível encerrar esta amizade agora.');
    }
  };

  return (
    <>
      <Header />
      <div className="body">
        <div className="center">
          <div className="body-content card friends-card">
            <div className="topo-page">
              <strong># Amigos</strong>
            </div>
            <p className="friends-description">
              Adicione jogadores para facilitar convites e acompanhar quem participa das suas turmas.
            </p>
            <form className="friends-form" onSubmit={handleSendRequest}>
              {error && (
                <p className="friends-error" role="alert">
                  {error}
                </p>
              )}
              {actionMessage && <p className="friends-success">{actionMessage}</p>}
              <label htmlFor="friend-email">Convidar por email</label>
              <input
                id="friend-email"
                type="email"
                placeholder="email@exemplo.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
              <button type="submit" className="btn-default" disabled={!email}>
                Enviar convite
              </button>
            </form>

            <div className="friends-section">
              <h3>Convites recebidos</h3>
              {loading ? (
                <span>Carregando...</span>
              ) : pendingReceived.length === 0 ? (
                <p>Não há convites pendentes.</p>
              ) : (
                pendingReceived.map((relation) => (
                  <div key={relation.id} className="friend-item">
                    <div>
                      <strong>{relation.otherUser.nome}</strong>
                      <p>{relation.otherUser.email}</p>
                    </div>
                    <div className="friends-actions">
                      <button
                        type="button"
                        className="btn-default"
                        onClick={() => handleRespond(relation.id, true)}
                      >
                        Aceitar
                      </button>
                      <button
                        type="button"
                        className="btn-outline"
                        onClick={() => handleRespond(relation.id, false)}
                      >
                        Recusar
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="friends-section">
              <h3>Convites enviados</h3>
              {loading ? (
                <span>Carregando...</span>
              ) : pendingSent.length === 0 ? (
                <p>Você não possui convites pendentes.</p>
              ) : (
                pendingSent.map((relation) => (
                  <div key={relation.id} className="friend-item">
                    <div>
                      <strong>{relation.otherUser.nome}</strong>
                      <p>{relation.otherUser.email}</p>
                    </div>
                    <button
                      type="button"
                      className="btn-outline"
                      onClick={() => handleCancel(relation.id)}
                    >
                      Cancelar
                    </button>
                  </div>
                ))
              )}
            </div>

            <div className="friends-section">
              <h3>Amigos</h3>
              {loading ? (
                <span>Carregando...</span>
              ) : activeFriends.length === 0 ? (
                <p>Convide jogadores para criar sua rede.</p>
              ) : (
                activeFriends.map((relation) => (
                  <div key={relation.id} className="friend-item">
                    <div className="friend-info">
                      <strong>{relation.otherUser.nome}</strong>
                      <p>{relation.otherUser.email}</p>
                    </div>
                    <div className="friend-menu-wrapper">
                      <button
                        type="button"
                        className="friend-menu-btn"
                        aria-haspopup="true"
                        aria-expanded={menuOpenFor === relation.id}
                        onClick={(event) => handleToggleFriendMenu(event, relation.id)}
                      >
                        ⋮
                      </button>
                      {menuOpenFor === relation.id && (
                        <div className="friend-menu">
                          <button
                            type="button"
                            className="danger"
                            onClick={(event) => handleEndFriendship(event, relation)}
                          >
                            Encerrar amizade
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Friends;
